import { useEffect, useRef, useState } from "react";
import { Camera, Check, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import { cn } from "@/lib/utils";

const PHOTO_CATEGORIES = [
  { id: "front_exterior", label: "Front exterior", hint: "The view as guests arrive" },
  { id: "back_exterior", label: "Back exterior", hint: "Garden, terrace or rear" },
  { id: "living_room", label: "Living room", hint: "Main lounge or seating area" },
  { id: "kitchen", label: "Kitchen", hint: "Cooking & dining area" },
  { id: "bedroom_1", label: "Bedroom 1", hint: "Primary bedroom" },
  { id: "bedroom_2", label: "Bedroom 2", hint: "Second bedroom" },
];

export default function PhotoCategoryUploader({ submissionId }) {
  const [files, setFiles] = useState([]);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const inputRefs = useRef({});

  const load = async () => {
    if (!submissionId) return;
    try {
      const res = await publicApi.get(`/submissions/${submissionId}/files`);
      setFiles(res.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const fileByCategory = (categoryId) =>
    files.find((f) => f.category === categoryId);

  const completedCount = PHOTO_CATEGORIES.filter((c) =>
    fileByCategory(c.id)
  ).length;

  const uploadFor = async (categoryId, file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please choose a photo (jpg, png, webp).");
      return;
    }
    setUploadingCategory(categoryId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", categoryId);
      await publicApi.post(`/submissions/${submissionId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `${PHOTO_CATEGORIES.find((c) => c.id === categoryId)?.label} uploaded.`
      );
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploadingCategory(null);
      if (inputRefs.current[categoryId]) {
        inputRefs.current[categoryId].value = "";
      }
    }
  };

  if (!submissionId) return null;

  return (
    <div data-testid="photo-category-uploader" className="border border-[#E3DED5] bg-white p-6 lg:p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Property photos</div>
          <h3 className="font-serif-display text-2xl text-[#2A1F1A] mt-2">
            Six photos to bring your home to life
          </h3>
          <p className="mt-2 text-sm text-[#6E6E70] max-w-xl">
            Please share one photo for each of the six rooms below. This is the
            minimum we need to begin shaping your listing.
          </p>
        </div>
        <div className="text-right">
          <div className="eyebrow">Progress</div>
          <div
            data-testid="photo-progress"
            className="font-serif-display text-3xl mt-1 text-[#2A1F1A]"
          >
            {completedCount}<span className="text-[#8E8F90]"> / 6</span>
          </div>
        </div>
      </div>

      <div className="mt-6 h-1 bg-[#F2EFE9] overflow-hidden">
        <div
          className="h-full bg-[#C03028] transition-all duration-500"
          style={{ width: `${(completedCount / 6) * 100}%` }}
        />
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PHOTO_CATEGORIES.map((cat) => {
          const existing = fileByCategory(cat.id);
          const isUploading = uploadingCategory === cat.id;
          return (
            <label
              key={cat.id}
              data-testid={`photo-slot-${cat.id}`}
              className={cn(
                "block cursor-pointer border transition-colors overflow-hidden",
                existing
                  ? "border-[#C03028]"
                  : "border-dashed border-[#E3DED5] hover:border-[#C03028]"
              )}
            >
              <input
                ref={(el) => (inputRefs.current[cat.id] = el)}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                disabled={isUploading}
                onChange={(e) => uploadFor(cat.id, e.target.files?.[0])}
                data-testid={`photo-input-${cat.id}`}
                className="hidden"
              />
              <div className="aspect-[4/3] bg-[#F2EFE9] relative flex items-center justify-center">
                {existing ? (
                  <>
                    <img
                      src={`${API}/files/${existing.id}/download`}
                      alt={cat.label}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <span className="absolute top-2 right-2 bg-[#C03028] text-white w-6 h-6 flex items-center justify-center">
                      <Check size={14} strokeWidth={3} />
                    </span>
                  </>
                ) : isUploading ? (
                  <Loader2 className="animate-spin text-[#C03028]" size={28} />
                ) : (
                  <div className="text-center text-[#8E8F90]">
                    <Camera className="mx-auto" size={22} />
                    <div className="text-[10px] uppercase tracking-[0.22em] mt-2">
                      Add photo
                    </div>
                  </div>
                )}
              </div>
              <div className="px-4 py-3 bg-white">
                <div className="flex items-center gap-2">
                  {existing ? (
                    <ImageIcon size={12} className="text-[#C03028]" />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-none bg-[#8E8F90] inline-block" />
                  )}
                  <div className="text-sm text-[#2A1F1A]">{cat.label}</div>
                </div>
                <div className="text-xs text-[#8E8F90] mt-1">{cat.hint}</div>
              </div>
            </label>
          );
        })}
      </div>

      {completedCount < 6 ? (
        <div
          data-testid="photo-progress-banner"
          className="mt-6 border-l-2 border-[#C03028] bg-[#FAF7F2] px-4 py-3 text-sm text-[#2A1F1A]"
        >
          <strong>{6 - completedCount}</strong> more photo{6 - completedCount === 1 ? "" : "s"}{" "}
          to share. We can&apos;t begin our review until all six are received.
        </div>
      ) : (
        <div
          data-testid="photo-complete-banner"
          className="mt-6 border-l-2 border-[#C03028] bg-[#FAF7F2] px-4 py-3 text-sm text-[#2A1F1A] flex items-center gap-2"
        >
          <Check size={16} className="text-[#C03028]" />
          Thank you — all six photos received. Our studio will be in touch shortly.
        </div>
      )}
    </div>
  );
}
